import React, { useState, useEffect } from 'react';
import {
  X, Calendar, Clock, User, BookOpen,
  FileText, AlertCircle, Check
} from 'lucide-react';
import { db, collection, getDocs } from '../../firebase';

import styles from '../CSS/NewReservationModal.module.css';

const NewReservationModal = ({
  show,
  onClose,
  onConfirm,
  selectedRange,
  setSelectedRange,
  user,
  space,
  weekDays,
  feedback,
  feedbackType,
  isSubmitting,
  canDelegateReservations = false
}) => {
  // Character limits
  const TITLE_MAX_LENGTH = 100;
  const DESCRIPTION_MAX_LENGTH = 350;

  // Estados para delegación de usuario
  const [delegatedUser, setDelegatedUser] = useState(null);
  const [delegatedUserSearch, setDelegatedUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [manualDelegation, setManualDelegation] = useState({
    name: '',
    email: ''
  });
  const [delegationMode, setDelegationMode] = useState('none'); // 'none', 'search', 'manual'

  // Para obtener la fecha en formato local
  const dateLabel = weekDays && selectedRange.dayIndex !== null && weekDays[selectedRange.dayIndex]
    ? new Date(weekDays[selectedRange.dayIndex].date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    : '';

  // Handle title change with character limit
  const handleTitleChange = (e) => {
    const value = e.target.value;
    if (value.length <= TITLE_MAX_LENGTH) {
      setSelectedRange((prev) => ({
        ...prev,
        title: value
      }));
    }
  };

  // Handle description change with character limit
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= DESCRIPTION_MAX_LENGTH) {
      setSelectedRange((prev) => ({
        ...prev,
        description: value
      }));
    }
  };

  // Función para buscar usuarios
  const searchUsers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const searchLower = searchTerm.toLowerCase();

      const querySnapshot = await getDocs(usersRef);
      const results = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data();

        // Construir nombre completo
        const firstName = userData.firstName || '';
        const lastName = userData.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        // El email ES el ID del documento
        const email = doc.id;

        // Buscar en firstName, lastName, nombre completo y email
        const firstNameLower = firstName.toLowerCase();
        const lastNameLower = lastName.toLowerCase();
        const fullNameLower = fullName.toLowerCase();
        const emailLower = email.toLowerCase();

        if (firstNameLower.includes(searchLower) ||
            lastNameLower.includes(searchLower) ||
            fullNameLower.includes(searchLower) ||
            emailLower.includes(searchLower)) {

          // Solo incluir usuarios con firstName
          if (firstName) {
            const userResult = {
              uid: doc.id,
              firstName: firstName,
              lastName: lastName,
              displayName: fullName || email,
              email: email
            };

            results.push(userResult);
          }
        }
      });

      // Ordenar por nombre
      results.sort((a, b) => a.displayName.localeCompare(b.displayName));

      setSearchResults(results.slice(0, 5));
    } catch (error) {
      console.error('Error buscando usuarios:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // useEffect para la búsqueda con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (delegationMode === 'search') {
        searchUsers(delegatedUserSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [delegatedUserSearch, delegationMode]);

  // Actualizar el selectedRange cuando cambie la delegación
  useEffect(() => {
    if (delegationMode === 'search' && delegatedUser) {
      setSelectedRange((prev) => ({
        ...prev,
        delegatedTo: {
          uid: delegatedUser.uid,
          displayName: delegatedUser.displayName,
          email: delegatedUser.email,
          delegationType: 'database'
        }
      }));
    } else if (delegationMode === 'manual' && manualDelegation.name.trim()) {
      setSelectedRange((prev) => ({
        ...prev,
        delegatedTo: {
          displayName: manualDelegation.name.trim(),
          email: manualDelegation.email.trim(),
          delegationType: 'manual'
        }
      }));
    } else if (delegationMode === 'none') {
      setSelectedRange((prev) => {
        const newRange = { ...prev };
        delete newRange.delegatedTo;
        return newRange;
      });
    }
  }, [delegationMode, delegatedUser, manualDelegation, setSelectedRange]);

  if (!show) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Nueva Reserva</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.modalInfo}>
            <div className={styles.infoItem}>
              <Calendar className={styles.infoIcon} size={20} />
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Espacio</span>
                <span className={styles.infoValue}>
                  {typeof space === 'string' ? space : space?.name}
                </span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <Calendar className={styles.infoIcon} size={20} />
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Fecha</span>
                <span className={styles.infoValue}>{dateLabel}</span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <Clock className={styles.infoIcon} size={20} />
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Horario</span>
                <span className={styles.infoValue}>
                  {selectedRange.startTime} - {selectedRange.endTime}
                </span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <User className={styles.infoIcon} size={20} />
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Reservado por</span>
                <span className={styles.infoValue}>{user?.displayName}</span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <BookOpen className={styles.inputIcon} />
                Título / Asignatura <span className={styles.requiredMarker}>*</span>
                <span className={styles.characterCount}>
                  {(selectedRange.title || '').length}/{TITLE_MAX_LENGTH}
                </span>
              </label>
              <input
                type="text"
                value={selectedRange.title || ''}
                onChange={handleTitleChange}
                className={styles.formInput}
                placeholder="Ej. Matemáticas 2°ESO"
                maxLength={TITLE_MAX_LENGTH}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <FileText className={styles.inputIcon} />
                Descripción (opcional)
                <span className={styles.characterCount}>
                  {(selectedRange.description || '').length}/{DESCRIPTION_MAX_LENGTH}
                </span>
              </label>
              <textarea
                value={selectedRange.description || ''}
                onChange={handleDescriptionChange}
                className={`${styles.formInput} ${styles.textarea}`}
                placeholder="Información adicional sobre la reserva"
                rows={3}
                maxLength={DESCRIPTION_MAX_LENGTH}
              />
            </div>

            {/* Sección de Delegación de Usuario */}
            {canDelegateReservations && (
              <div className={styles.delegationSection}>
                <div className={styles.delegationHeader}>
                  <label className={styles.formLabel}>
                    <User className={styles.inputIcon} />
                    Asignar Reserva
                  </label>
                  <p className={styles.delegationDescription}>
                    Asigna esta reserva a otro usuario
                  </p>
                </div>

                <div className={styles.delegationModes}>
                  <button
                    type="button"
                    className={`${styles.delegationModeBtn} ${delegationMode === 'none' ? styles.delegationModeActive : ''}`}
                    onClick={() => {
                      setDelegationMode('none');
                      setDelegatedUser(null);
                      setDelegatedUserSearch('');
                      setManualDelegation({ name: '', email: '' });
                      setSearchResults([]);
                    }}
                  >
                    Sin asignar
                  </button>
                  <button
                    type="button"
                    className={`${styles.delegationModeBtn} ${delegationMode === 'search' ? styles.delegationModeActive : ''}`}
                    onClick={() => {
                      setDelegationMode('search');
                      setManualDelegation({ name: '', email: '' });
                    }}
                  >
                    Buscar usuario
                  </button>
                  <button
                    type="button"
                    className={`${styles.delegationModeBtn} ${delegationMode === 'manual' ? styles.delegationModeActive : ''}`}
                    onClick={() => {
                      setDelegationMode('manual');
                      setDelegatedUser(null);
                      setDelegatedUserSearch('');
                      setSearchResults([]);
                    }}
                  >
                    Entrada manual
                  </button>
                </div>

                {delegationMode === 'search' && (
                  <div className={styles.userSearchContainer}>
                    <div className={styles.formGroup}>
                      <input
                        type="text"
                        value={delegatedUserSearch}
                        onChange={(e) => setDelegatedUserSearch(e.target.value)}
                        className={styles.formInput}
                        placeholder="Buscar por nombre..."
                      />
                    </div>

                    {isSearching && (
                      <div className={styles.searchingIndicator}>
                        Buscando usuarios...
                      </div>
                    )}

                    {searchResults.length > 0 && (
                      <div className={styles.searchResults}>
                        {searchResults.map((result) => (
                          <button
                            key={result.uid}
                            type="button"
                            className={`${styles.userResultItem} ${delegatedUser?.uid === result.uid ? styles.userResultSelected : ''}`}
                            onClick={() => {
                              setDelegatedUser(result);
                              setDelegatedUserSearch(result.displayName);
                              setSearchResults([]);
                            }}
                          >
                            <User size={16} className={styles.userResultIcon} />
                            <div className={styles.userResultInfo}>
                              <span className={styles.userResultName}>{result.displayName}</span>
                              <span className={styles.userResultEmail}>{result.email}</span>
                            </div>
                            {delegatedUser?.uid === result.uid && (
                              <Check size={16} className={styles.userResultCheck} />
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {delegatedUser && (
                      <div className={styles.selectedUserBadge}>
                        <User size={14} />
                        <span>Asignado a: {delegatedUser.displayName}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setDelegatedUser(null);
                            setDelegatedUserSearch('');
                          }}
                          className={styles.removeUserBtn}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {delegationMode === 'manual' && (
                  <div className={styles.manualDelegationContainer}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        Nombre del usuario <span className={styles.requiredMarker}>*</span>
                      </label>
                      <input
                        type="text"
                        value={manualDelegation.name}
                        onChange={(e) => setManualDelegation(prev => ({ ...prev, name: e.target.value }))}
                        className={styles.formInput}
                        placeholder="Nombre completo"
                        maxLength={100}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        Email (opcional)
                      </label>
                      <input
                        type="email"
                        value={manualDelegation.email}
                        onChange={(e) => setManualDelegation(prev => ({ ...prev, email: e.target.value }))}
                        className={styles.formInput}
                        placeholder="correo@ejemplo.com"
                      />
                      <p className={styles.emailNote}>
                        Si proporcionas el email, el usuario recibirá una notificación
                      </p>
                    </div>

                    {manualDelegation.name && (
                      <div className={styles.manualDelegationPreview}>
                        <User size={14} />
                        <span>
                          Se asignará a: {manualDelegation.name}
                          {manualDelegation.email && ` (${manualDelegation.email})`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {feedback && (
            <div className={`${styles.feedback} ${styles[feedbackType]}`}>
              {feedbackType === 'error' ? (
                <AlertCircle className={styles.feedbackIcon} />
              ) : (
                <Check className={styles.feedbackIcon} />
              )}
              {feedback}
            </div>
          )}
        </div>
        <div className={styles.modalActions}>
          <button onClick={onClose} className={styles.btnSecondary}>
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className={styles.btnPrimary}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creando...' : 'Confirmar Reserva'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewReservationModal;